import { useEffect, useMemo, useRef } from 'react';
import { type Product } from '@/services/products/types';
import { type TrolleyLineItem } from '@/types/api-types';
import { useTrolleyContext } from '@/context/TrolleyContext/TrolleyContext';
import { Carousel } from '@johnlewispartnership/wtr-ingredients/ingredients/Carousel';
import { Typography } from '@johnlewispartnership/wtr-ingredients/foundations/typography';
import ProductPod from '@/components/ProductPod';
import { fireGAEvent, getItemListDetails, transformItem } from '@/services/analytics';
import styles from './ProductRecommendations.module.scss';

export function ProductRecommendations({
  recommendedProducts,
}: {
  recommendedProducts: Product[];
}) {
  const { addItem, itemBeingUpdated, loading, trolley, updateQuantity } = useTrolleyContext();
  const itemsByProductSKU = useMemo(
    () => Object.fromEntries(trolley?.lineItems.map((lineItem) => [lineItem.sku, lineItem]) ?? []),
    [trolley],
  ) as Record<string, TrolleyLineItem>;

  const viewCarouselListEventFiredRef = useRef(false);
  useEffect(() => {
    const fireCarouselGAEvent = () => {
      if (recommendedProducts.length > 0 && !viewCarouselListEventFiredRef.current) {
        const { id, name } = getItemListDetails();
        const listItems = recommendedProducts.map((recommendedProduct, index) =>
          transformItem(recommendedProduct, { index }),
        );
        fireGAEvent('view_item_list', {
          currency: 'GBP',
          item_list_id: id,
          item_list_name: name,
          items: listItems,
        });
        viewCarouselListEventFiredRef.current = true;
      }
    };
    fireCarouselGAEvent();
  }, [recommendedProducts, viewCarouselListEventFiredRef]);

  return (
    <div className={styles.recommendedProducts}>
      <Typography className={styles.heading} component="h2">
        You might like this
      </Typography>
      <Carousel ariaName="recommended-product" disableScrollTo>
        {recommendedProducts.map((product, index) => (
          <ProductPod
            addItem={addItem}
            horizontalGrid
            key={`recommended-product-${product.sku}`}
            lineItem={itemsByProductSKU[product.sku]}
            listType="Carousel"
            loading={!!loading && itemBeingUpdated === product.sku}
            position={index}
            product={product}
            titleElement="h3"
            updateProductQuantity={updateQuantity}
          />
        ))}
      </Carousel>
    </div>
  );
}
